import type {
  ContractHistoryQueryParams,
  ContractHistoryResponse,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractHistory } from './fetchContractHistory';

/**
 * Generates the query options for the contract history query.
 *
 * @param contractId - The ID of the contract.
 * @param queryParams - Pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractHistoryQueryOptions = <
  TData = ContractHistoryResponse,
>({
  contractId,
  queryParams,
  options,
}: {
  contractId: UUID | undefined;
  queryParams?: ContractHistoryQueryParams;
  options?: Omit<
    UseQueryOptions<ContractHistoryResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractHistory(contractId, queryParams, { signal })
      : skipToken,
    queryKey: queryKeysContract.getHistory(contractId ?? ''),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the version history of a contract.
 *
 * @param contractId - The ID of the contract.
 * @param queryParams - Pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_history_contracts__contract_id__history_get
 */
export const useQueryContractHistory = (
  contractId: UUID | undefined,
  queryParams?: ContractHistoryQueryParams,
  options?: Omit<
    UseQueryOptions<ContractHistoryResponse, Error, ContractHistoryResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getContractHistoryQueryOptions<ContractHistoryResponse>({
      contractId,
      queryParams,
      options,
    }),
  );
